import { useQuery } from '@tanstack/react-query';
import { flows, identity, tickets } from '@repletoryreach/sdk-api';
import type { ChecklistItem, Guide, GuideLink, TourStep } from '../../app/guides/types';
import { TICKETS_KEY, useTeamsList, useTicketSettings, useTicketTypes } from './lib/data';

// The Tickets guide (8 October 2026): the tour, five how-tos, the written guide and the setup checklist. The words
// are in the catalogue under guide.tickets.*; the pages it links to are the console's own routes.

const L = (key: string, to: string): GuideLink => ({ labelKey: `guide.tickets.link.${key}`, to });
const LINKS = {
  queue: L('queue', '/tickets'),
  board: L('board', '/tickets/board'),
  tasks: L('tasks', '/tickets/tasks'),
  reports: L('reports', '/tickets/reports'),
  settings: L('settings', '/tickets/settings'),
  types: L('typesTab', '/tickets/settings?section=types'),
  statuses: L('statusesTab', '/tickets/settings?section=statuses'),
  priorities: L('prioritiesTab', '/tickets/settings?section=priorities'),
  sla: L('slaTab', '/tickets/settings?section=sla'),
  automations: L('automationsTab', '/tickets/settings?section=automations'),
  updates: L('updatesTab', '/tickets/settings?section=updates'),
  hours: L('hours', '/settings/organisation'),
  teams: L('teams', '/settings/team'),
  flows: L('flows', '/flows'),
  inbox: L('inbox', '/inbox'),
  templates: L('templates', '/templates'),
  notifications: L('notifications', '/settings/notifications'),
  bookings: L('bookings', '/bookings/services'),
  billing: L('billing', '/billing'),
  howCreate: { labelKey: 'guide.tickets.link.howCreate', tour: 'create' },
  howWork: { labelKey: 'guide.tickets.link.howWork', tour: 'work' },
  howTypes: { labelKey: 'guide.tickets.link.howTypes', tour: 'types' },
  howAutomation: { labelKey: 'guide.tickets.link.howAutomation', tour: 'automation' },
  howSla: { labelKey: 'guide.tickets.link.howSla', tour: 'sla' }
};

const step = (id: string, route: string | undefined, target?: string, links?: GuideLink[]): TourStep => ({
  route, target, links, titleKey: `guide.tickets.tour.${id}.title`, bodyKey: `guide.tickets.tour.${id}.body`
});
const how = (tour: string, id: string, route: string | undefined, target: string, opts: { open?: string; links?: GuideLink[] } = {}): TourStep => ({
  route, target, open: opts.open, links: opts.links,
  titleKey: `guide.tickets.howto.${tour}.${id}.title`, bodyKey: `guide.tickets.howto.${tour}.${id}.body`
});
const section = (id: string, paragraphs: number, notes: number, links: GuideLink[]) => ({
  id,
  titleKey: `guide.tickets.s.${id}.title`,
  bodyKeys: Array.from({ length: paragraphs }, (_, i) => `guide.tickets.s.${id}.p${i + 1}`),
  noteKeys: Array.from({ length: notes }, (_, i) => `guide.tickets.s.${id}.note${i + 1}`),
  links
});

const SETTINGS = '/tickets/settings?section=';

/** Whether the business has set up each part, from what is really there. Steps that cannot be read are ticked by hand. */
function useTicketsChecklist(): { items: ChecklistItem[]; loading: boolean } {
  const hours = useQuery({ queryKey: ['business-hours'], queryFn: () => identity.getBusinessHours(), staleTime: 60_000 });
  const teams = useTeamsList();
  const types = useTicketTypes();
  const settings = useTicketSettings();
  const sla = useQuery({ queryKey: [TICKETS_KEY, 'sla'], queryFn: () => tickets.listSlaPolicies(), staleTime: 60_000 });
  const rules = useQuery({ queryKey: [TICKETS_KEY, 'rules'], queryFn: () => tickets.listRules(), staleTime: 60_000 });
  const flowList = useQuery({ queryKey: ['flows', 'guide'], queryFn: () => flows.listFlows(), staleTime: 60_000 });

  const activeTypes = (types.data?.data ?? []).filter((x) => x.active);
  const hasTicketStep = (flowList.data?.data ?? []).some((f) => f.is_active && JSON.stringify(f.definition ?? {}).includes('"type":"ticket"'));
  const item = (id: string, to: string, done: boolean | undefined, optional = false): ChecklistItem => ({
    id, to, done, optional, titleKey: `guide.tickets.check.${id}.title`, hintKey: `guide.tickets.check.${id}.hint`
  });
  const items = [
    item('hours', '/settings/organisation', hours.data ? hours.data.data.configured : undefined),
    // The general type is made for every business; more than one, or one with its own fields, means it was set up.
    item('types', `${SETTINGS}types`, types.data ? activeTypes.length > 1 || activeTypes.some((x) => x.fields.length > 0) : undefined),
    item('sla', `${SETTINGS}sla`, sla.data ? sla.data.data.some((p) => p.active) : undefined),
    item('automation', `${SETTINGS}automations`, rules.data ? rules.data.data.some((r) => r.active) : undefined),
    item('chatbot', '/flows', flowList.data ? hasTicketStep : undefined),
    // What clients are told is on by default; only the business can say it has checked the wording.
    item('updates', `${SETTINGS}updates`, undefined),
    item('teams', '/settings/team', teams.data ? (teams.data.data?.length ?? 0) > 0 : undefined, true),
    item('csat', `${SETTINGS}satisfaction`, settings.data ? settings.data.data.csat_enabled : undefined, true)
  ];
  return { items, loading: [hours, types, settings, sla, rules, flowList].some((q) => q.isPending) };
}

export const TICKETS_GUIDE: Guide = {
  id: 'tickets',
  version: 1,
  titleKey: 'guide.tickets.title',
  introKey: 'guide.tickets.intro',
  routes: ['/tickets'],
  tour: [
    step('welcome', undefined, undefined, [LINKS.hours, LINKS.teams]),
    step('views', '/tickets', 'tickets-views'),
    step('filters', '/tickets', 'tickets-filters'),
    step('new', '/tickets', 'tickets-new', [LINKS.howCreate]),
    step('list', '/tickets', 'tickets-list', [LINKS.howWork]),
    step('board', '/tickets/board', 'tickets-board'),
    step('tasks', '/tickets/tasks', 'tickets-tasks'),
    step('reports', '/tickets/reports', 'tickets-reports'),
    step('settings', `${SETTINGS}general`, 'tickets-settings-tabs'),
    step('types', `${SETTINGS}types`, 'tickets-settings-types', [LINKS.howTypes]),
    step('statuses', `${SETTINGS}statuses`, 'tickets-settings-statuses'),
    step('sla', `${SETTINGS}sla`, 'tickets-settings-sla', [LINKS.howSla, LINKS.hours]),
    step('automations', `${SETTINGS}automations`, 'tickets-settings-automations', [LINKS.howAutomation]),
    step('updates', `${SETTINGS}updates`, 'tickets-settings-updates'),
    step('comeIn', undefined, undefined, [LINKS.flows, LINKS.inbox]),
    step('help', '/tickets', 'tickets-help')
  ],
  howTos: [
    {
      id: 'create',
      titleKey: 'guide.tickets.howto.create.title',
      hintKey: 'guide.tickets.howto.create.hint',
      steps: [
        how('create', 'subject', '/tickets', 'create-subject', { open: 'tickets-new' }),
        how('create', 'description', '/tickets', 'create-description', { open: 'tickets-new' }),
        how('create', 'type', '/tickets', 'create-type', { open: 'tickets-new' }),
        how('create', 'team', '/tickets', 'create-team', { open: 'tickets-new' }),
        how('create', 'save', '/tickets', 'create-save', { open: 'tickets-new', links: [LINKS.inbox] })
      ]
    },
    {
      id: 'work',
      titleKey: 'guide.tickets.howto.work.title',
      hintKey: 'guide.tickets.howto.work.hint',
      steps: [
        how('work', 'open', '/tickets', 'tickets-list'),
        how('work', 'header', undefined, 'ticket-header'),
        how('work', 'timeline', undefined, 'ticket-timeline'),
        how('work', 'composer', undefined, 'ticket-composer', { links: [LINKS.templates] }),
        how('work', 'sla', undefined, 'ticket-sla'),
        how('work', 'tasks', undefined, 'ticket-tasks', { links: [LINKS.tasks, LINKS.bookings] })
      ]
    },
    {
      id: 'types',
      titleKey: 'guide.tickets.howto.types.title',
      hintKey: 'guide.tickets.howto.types.hint',
      steps: [
        how('types', 'list', `${SETTINGS}types`, 'tickets-settings-types'),
        how('types', 'name', `${SETTINGS}types`, 'type-name', { open: 'tickets-types-add' }),
        how('types', 'defaults', `${SETTINGS}types`, 'type-defaults', { open: 'tickets-types-add', links: [LINKS.teams] }),
        how('types', 'fields', `${SETTINGS}types`, 'type-fields', { open: 'tickets-types-add' }),
        how('types', 'save', `${SETTINGS}types`, 'type-save', { open: 'tickets-types-add' })
      ]
    },
    {
      id: 'automation',
      titleKey: 'guide.tickets.howto.automation.title',
      hintKey: 'guide.tickets.howto.automation.hint',
      steps: [
        how('automation', 'list', `${SETTINGS}automations`, 'tickets-settings-automations'),
        how('automation', 'name', `${SETTINGS}automations`, 'rule-name', { open: 'tickets-rules-add' }),
        how('automation', 'when', `${SETTINGS}automations`, 'rule-when', { open: 'tickets-rules-add' }),
        how('automation', 'if', `${SETTINGS}automations`, 'rule-if', { open: 'tickets-rules-add' }),
        how('automation', 'then', `${SETTINGS}automations`, 'rule-then', { open: 'tickets-rules-add' }),
        how('automation', 'switches', `${SETTINGS}automations`, 'rule-switches', { open: 'tickets-rules-add' }),
        how('automation', 'save', `${SETTINGS}automations`, 'rule-save', { open: 'tickets-rules-add' })
      ]
    },
    {
      id: 'sla',
      titleKey: 'guide.tickets.howto.sla.title',
      hintKey: 'guide.tickets.howto.sla.hint',
      steps: [
        how('sla', 'list', `${SETTINGS}sla`, 'tickets-settings-sla', { links: [LINKS.hours] }),
        how('sla', 'match', `${SETTINGS}sla`, 'sla-match', { open: 'tickets-sla-add' }),
        how('sla', 'first', `${SETTINGS}sla`, 'sla-first', { open: 'tickets-sla-add' }),
        how('sla', 'resolution', `${SETTINGS}sla`, 'sla-resolution', { open: 'tickets-sla-add' }),
        how('sla', 'hours', `${SETTINGS}sla`, 'sla-hours', { open: 'tickets-sla-add', links: [LINKS.hours] }),
        how('sla', 'save', `${SETTINGS}sla`, 'sla-save', { open: 'tickets-sla-add' })
      ]
    }
  ],
  sections: [
    section('overview', 3, 1, [LINKS.queue, LINKS.board]),
    section('comeIn', 4, 2, [LINKS.howCreate, LINKS.inbox, LINKS.flows, LINKS.howAutomation]),
    section('queue', 3, 1, [LINKS.queue, LINKS.board]),
    section('work', 4, 2, [LINKS.howWork, LINKS.templates]),
    section('tasks', 2, 1, [LINKS.tasks, LINKS.bookings]),
    section('types', 2, 1, [LINKS.howTypes, LINKS.types]),
    section('statuses', 3, 1, [LINKS.statuses, LINKS.updates]),
    section('priorities', 1, 0, [LINKS.priorities]),
    section('sla', 3, 2, [LINKS.howSla, LINKS.sla, LINKS.hours]),
    section('automations', 3, 2, [LINKS.howAutomation, LINKS.automations]),
    section('updates', 2, 1, [LINKS.updates]),
    section('access', 2, 1, [LINKS.settings, LINKS.teams, LINKS.notifications]),
    section('reports', 1, 0, [LINKS.reports]),
    section('trouble', 5, 0, [LINKS.templates, LINKS.automations, LINKS.billing])
  ],
  useChecklist: useTicketsChecklist
};
